import type { NavigationBarV1Attributes } from '@/features/navigation-bar-v1/navigation-bar-v1-type';
import type { NavigationBarProps } from '@/features/navigation-bar-v1-desktop/navigation-bar-v1-desktop';

import React from 'react';

import { Link, RawImage } from '@/core/features';
import useScrollIntoViewHorizontal from '@/core/features/scroll/use-scroll-into-view-horizontal';
import color from '@/core/features/styles/color';
import { useCookieConsentBannerVisibleState } from '@/core/features/wireframe/use-wireframe-consent-banner-visible';

export type NavigationBarV1Props = NavigationBarV1Attributes;

export default function NavigationBarV1({ items }: NavigationBarProps) {
    const isCookieConsentBannerVisible = useCookieConsentBannerVisibleState();

    const activeChipRef = React.useRef<HTMLDivElement>(null);
    const navbarRef = React.useRef<HTMLDivElement>(null);
    const [clickedItem, setClickedItem] = React.useState<null | number>(null);

    useScrollIntoViewHorizontal({
        containerRef: navbarRef,
        onItemClick: () => setClickedItem(null),
        targetElementRef: activeChipRef,
    });

    return (
        <div
            className={'no-scrollbar width-100 text-white flex bg-check24Blue overflow-x-scroll'}
            ref={navbarRef}
            // eslint-disable-next-line no-inline-styles/no-inline-styles
            style={{ height: 55 }}
        >
            <nav className={'flex-center padding-10'}>
                {items.map((item, index) => {
                    return (
                        <Link
                            href={item.linkUrl}
                            key={item.title}
                            linkType={'vertical'}
                            relType={'follow'}
                        >
                            <div
                                className={
                                    'flex-center width-max-content padding-right-15 padding-left-15 border-radius-20 font-size-14 border-transparent'
                                }
                                onClick={() => setClickedItem(index)}
                                ref={item.isHighlighted ? activeChipRef : null}
                                style={{
                                    backgroundColor:
                                        item.isHighlighted || clickedItem === index ? color('foam-01') : undefined,
                                    borderColor: item.isHighlighted ? color('white') : undefined,
                                    // prevents jumping on different screen resolutions
                                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                                    height: 35,
                                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                                    marginTop: '-0.2px',
                                }}
                            >
                                <div
                                    className={'margin-right-10 flex'}
                                    style={{
                                        // eslint-disable-next-line no-inline-styles/no-inline-styles
                                        minWidth: 14.5,
                                    }}
                                >
                                    <RawImage
                                        alt={`${item.title} icon`}
                                        fetchPriority={index < 4 ? 'high' : 'low'}
                                        height={32}
                                        preload={isCookieConsentBannerVisible ? index < 4 : true}
                                        style={{
                                            padding: item.padding ?? undefined,
                                        }}
                                        url={item.iconUrl}
                                        width={32}
                                    />
                                </div>
                                <div>{item.title}</div>
                            </div>
                        </Link>
                    );
                })}
            </nav>
        </div>
    );
}
